import { injectable } from 'inversify'

import { PersistentStoreBase } from '@writefull/core'

import { Session } from '@domain/session'
import { User } from '@domain/user'

export interface SessionStoreState {
  isInitialized: boolean
  isLoading: boolean
  session: Session | undefined
  user: User | undefined
  hasError: boolean
  lastLogin: number | undefined
  isRefreshingOverleafStatus: boolean
  hasRefreshedOverleafStatus: boolean
  hasFailedRefreshOverleafStatus: boolean
}

export interface SessionStoreActions {
  setIsInitialized: () => void
  start: () => void
  finish: (session: Session, user: User) => void
  refresh: (idToken: string, refreshToken: string, expiresAt: number) => void
  setStartRefreshOverleafStatus: () => void
  setFinishRefreshOverleafStatus: () => void
  setFailedRefreshOverleafStatus: () => void
  fail: () => void
  clean: () => void
  cleanError: () => void
  setLastLogin: () => void
}

const initialState: SessionStoreState = {
  isInitialized: false,
  isLoading: false,
  session: undefined,
  user: undefined,
  hasError: false,
  lastLogin: undefined,
  isRefreshingOverleafStatus: false,
  hasRefreshedOverleafStatus: false,
  hasFailedRefreshOverleafStatus: false,
}

const actions = (
  set: (fn: (state: SessionStoreState) => SessionStoreState) => void,
): SessionStoreActions => ({
  setIsInitialized: () => set((s) => ({ ...s, isInitialized: true })),
  start: () =>
    set((s) => ({ ...s, session: undefined, user: undefined, isLoading: true, hasError: false })),
  finish: (session, user) =>
    set((s) => ({ ...s, session, user, isLoading: false, hasError: false })),
  refresh: (idToken, refreshToken, expiresAt) =>
    set((s) => ({
      ...s,
      session: { ...s.session!, idToken, refreshToken, expiresAt },
    })),
  setStartRefreshOverleafStatus: () => set((s) => ({ ...s, isRefreshingOverleafStatus: true })),
  setFinishRefreshOverleafStatus: () =>
    set((s) => ({ ...s, isRefreshingOverleafStatus: false, hasRefreshedOverleafStatus: true })),
  setFailedRefreshOverleafStatus: () =>
    set((s) => ({ ...s, isRefreshingOverleafStatus: false, hasFailedRefreshOverleafStatus: true })),
  fail: () =>
    set((s) => ({ ...s, session: undefined, user: undefined, isLoading: false, hasError: true })),
  clean: () =>
    set((s) => ({
      ...s,
      session: undefined,
      user: undefined,
      isLoading: false,
      hasError: false,
    })),
  cleanError: () => set((s) => ({ ...s, hasError: false })),
  setLastLogin: () => set((s) => ({ ...s, lastLogin: Date.now() })),
})

@injectable()
export class SessionStore extends PersistentStoreBase<SessionStoreState, SessionStoreActions> {
  constructor() {
    super(initialState, actions, {
      version: 1,
      storageName: 'session',
      propsToPersist: ['session', 'lastLogin'],
    })
  }
}

export const sessionStore = new SessionStore()
const useSessionStore = sessionStore.getStore()
export default useSessionStore
